import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { usersApi, ApiError } from '../lib/api';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; avatarUrl?: string }) =>
      usersApi.updateProfile(data),
    onSuccess: () => {
      setSuccess('プロフィールを更新しました');
      setError('');
      refreshUser();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('更新に失敗しました');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    updateMutation.mutate({ name });
  };

  const resizeImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        const size = 256;
        canvas.width = size;
        canvas.height = size;

        const minDim = Math.min(img.width, img.height);
        const sx = (img.width - minDim) / 2;
        const sy = (img.height - minDim) / 2;

        ctx?.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('画像の変換に失敗しました'));
          },
          'image/webp',
          0.85
        );
      };

      img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const resized = await resizeImage(file);
      const formData = new FormData();
      formData.append('file', resized, 'avatar.webp');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/upload/avatars`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('アップロードに失敗しました');

      const { url } = await response.json();
      await updateMutation.mutateAsync({ avatarUrl: url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-theme-primary">プロフィール変更</h1>

      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        {success && (
          <div className="mb-6 p-4 bg-green-500 bg-opacity-20 border border-green-500 rounded-lg text-green-500">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500">
            {error}
          </div>
        )}

        {/* アバター */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-accent text-navy-900 flex items-center justify-center text-3xl font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-8 h-8 bg-theme-card border border-theme rounded-full flex items-center justify-center hover:bg-theme-hover transition-colors"
            >
              {uploading ? '...' : '📷'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
          <p className="text-theme-secondary text-sm mt-2">
            クリックして画像を変更
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">
              メールアドレス
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 bg-theme-hover border border-theme rounded-lg text-theme-secondary cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">
              名前
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="w-full py-3 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {updateMutation.isPending ? '保存中...' : '変更を保存'}
          </button>
        </form>
      </div>
    </div>
  );
}
